/**
 * English (US) catalog for the admin SPA. Partial catalogs are legal:
 * anything missing falls back to es-MX (see index.ts), so this file can be
 * completed incrementally.
 */

import type { AdminMessages } from './index.js';

export const adminEnUS: Partial<AdminMessages> = {
  'app.title': 'Equinox — Admin',

  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.loading': 'Loading…',
  'common.error': 'Something went wrong',
  'common.retry': 'Retry',
  'common.required': 'Required field',
  'common.search': 'Search',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.actions': 'Actions',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.back': 'Back',
  'common.signOut': 'Sign out',
  'common.status': 'Status',
  'common.all': 'All',
  'common.none': 'None',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.saved': 'Changes saved',

  'nav.products': 'Products',
  'nav.categories': 'Categories',
  'nav.discountReasons': 'Discount reasons',
  'nav.staff': 'Staff',
  'nav.terminals': 'Terminals',
  'nav.paymentTerminals': 'Payment terminals',
  'nav.reports': 'Reports',
  'nav.shifts': 'Shift reconciliation',
  'nav.sync': 'Sync health',
  'nav.ticket': 'Ticket',
  'terminals.timeZone': 'Time zone',
  'terminals.timeZoneTitle': 'Terminal time zone',
  'terminals.timeZoneLabel': 'Time zone (IANA name)',
  'terminals.timeZoneHint': 'For example America/Mexico_City. Type to search the list.',
  'terminals.timeZoneReported': 'The device reports zone {zone}. Set a manual one only if that is wrong.',
  'terminals.timeZoneNotReported': 'The device has not reported its time zone yet; the business zone is used.',
  'terminals.timeZoneUseDevice': 'Use the device zone',
  'terminals.timeZoneManual': 'manual',
  'login.downloadApk': 'Download the terminal app (APK)',

  'login.title': 'Admin sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.error': 'Incorrect email or password',
  'login.networkError': 'Could not reach the server. Try again',
  'login.sessionUnreachable': 'Could not confirm the session. Retrying on its own; the session is still open',

  'products.title': 'Products',
  'products.subtitle': 'Catalog, prices and images',
  'products.search': 'Search by name or SKU',
  'products.category': 'Category',
  'products.allCategories': 'All categories',
  'products.new': 'New product',
  'products.name': 'Name',
  'products.sku': 'SKU',
  'products.price': 'Price',
  'products.cost': 'Cost',
  'products.options.title': 'Options (flavors)',
  'products.options.enable': 'This product is sold by options',
  'products.options.mixHint': 'Each option has its own price, cost and stock. If several are chosen, the dearest is charged.',
  'products.options.label': 'Question when choosing',
  'products.options.labelPlaceholder': 'Flavor',
  'products.options.max': 'Maximum to choose',
  'products.options.maxPlaceholder': 'No limit',
  'products.options.name': 'Option',
  'products.options.add': 'Add option',
  'products.options.remove': 'Remove',
  'products.options.moveUp': 'Move up',
  'products.options.moveDown': 'Move down',
  'products.options.fromPrice': 'Price (from)',
  'products.options.priceDerived': 'The product price is the cheapest active option price.',
  'products.options.maxInvalid': "Enter a whole number from 1 up, or leave it empty",
  'products.options.savedButStock': "The product is already saved, but an option's stock could not be adjusted: save again.",
  'products.options.problem.noneActive': "Keep at least one option active.",
  'products.options.stockOnOptions': "Stock is counted per option: edit the product",
  'products.options.addon': "Extra (added to the price; not a flavor)",
  'products.options.addonPrice': "Extra price",
  'products.options.addonNote': "An extra has no cost or stock: it is charged on top of the flavor.",
  'products.options.group': "Column",
  'products.options.groupPlaceholder': "Sweet, Cheeses, Savory…",
  'products.options.mixLegend': "Mix price",
  'products.options.mixMin': "From how many flavours",
  'products.options.mixNone': "No mix price",
  'products.options.mixPrice': "Price from that number",
  'products.options.mixInvalid': "Fill both: a whole number from 2 up and the price, or leave both empty",
  'products.options.addonsMax': "Maximum extras",
  'products.needsKitchen': "Goes to the kitchen (appears on the kitchen order)",
  'products.needsKitchenHint': "Untick it for jars, snacks and anything not prepared: it creates no kitchen order.",
  'products.options.problem.none': 'Add at least one option, or turn options off.',
  'products.options.problem.nameRequired': 'Every option needs a name.',
  'products.options.problem.priceRequired': 'Every option needs a price.',
  'products.options.problem.duplicateName': 'Two options cannot share a name.',
  'products.options.problem.invalidNumber': 'Check the amounts and stock of the options.',
  'products.fromPriceCell': '{price}+',
  'products.fromPriceLead': 'From',
  'products.costHint': 'Unit cost, optional. Admin-only; it never reaches the terminals.',
  'products.priceCentsPreview': 'Will be saved as {cents} cents',
  'products.priceInvalid': 'Enter a valid amount, up to two decimals',
  'products.image': 'Image',
  'products.imageUrl': 'Image URL',
  'products.imageHelp': 'Optional. When there is no image, the product initial is shown instead',
  'products.sortOrder': 'Order',
  'products.save': 'Save product',
  'products.saved': 'Product saved',
  'products.deactivate': 'Deactivate',
  'products.deactivateConfirm': 'The product will stop showing at the register. You can reactivate it later',
  'products.activate': 'Reactivate',
  'products.noResults': 'No product matches the search',
  'products.editPrice': 'Edit price',
  'products.cancelEdit': 'Cancel edit',

  'products.import.action': 'Import CSV',
  'products.import.title': 'Import products from CSV',
  'products.import.help': 'Required columns: sku, name, category, price, image_url (optional)',
  'products.import.selectFile': 'Select file',
  'products.import.dryRunButton': 'Preview changes',
  'products.import.previewTitle': 'Preview — nothing has been saved yet',
  'products.import.row': 'Row',
  'products.import.willCreate': 'Will create',
  'products.import.willUpdate': 'Will update',
  'products.import.willSkip': 'Will skip',
  'products.import.errors': 'Errors',
  'products.import.noErrors': 'No errors',
  'products.import.confirmButton': 'Confirm and import',
  'products.import.confirming': 'Importing…',
  'products.import.success': 'Import complete: {count} rows applied',
  'products.import.hasErrorsWarning': 'Some rows have errors. Those rows will not be applied',
  'products.import.selectFileFirst': 'Select a CSV file first',

  'categories.title': 'Categories',
  'categories.subtitle': 'Color and order shown at the register',
  'categories.new': 'New category',
  'categories.name': 'Name',
  'categories.color': 'Color',
  'categories.sortOrder': 'Order',
  'categories.preview': 'This is how it will look at the register',
  'categories.save': 'Save category',
  'categories.deactivate': 'Deactivate',
  'categories.deactivateConfirm': 'The category will stop showing at the register',
  'categories.activate': 'Reactivate',
  'categories.noProducts': 'No products',

  'discountReasons.title': 'Discount reasons',
  'discountReasons.subtitle': 'A discount is never applied without a reason. These are the ones available',
  'discountReasons.new': 'New reason',
  'discountReasons.label': 'Reason',
  'discountReasons.requiresNote': 'Requires an additional note',
  'discountReasons.save': 'Save reason',
  'discountReasons.deactivate': 'Deactivate',
  'discountReasons.deactivateConfirm': 'The reason will no longer be available at the register',

  'staff.title': 'Staff',
  'staff.subtitle': 'Codes, discount limits and access',
  'staff.new': 'New staff member',
  'staff.code': 'Staff code',
  'staff.displayName': 'Name',
  'staff.email': 'Email (optional, admins only)',
  'staff.role': 'Role',
  'staff.roleStaff': 'Staff',
  'staff.roleAdmin': 'Admin',
  'staff.discountCeiling': 'Discount ceiling (%)',
  'staff.discountCeilingHelp': '0 to 100 %. Larger discounts require admin authorization',
  'staff.pinSection': 'Terminal access PIN',
  'staff.pin': 'New PIN (4 to 6 digits)',
  'staff.pinConfirm': 'Confirm PIN',
  'staff.pinMismatch': 'The PINs do not match',
  'staff.pinInvalid': 'The PIN must be 4 to 6 digits',
  'staff.pinHashedNotice': 'The PIN is stored hashed. It is never shown or retrievable, only replaced',
  'staff.pinNeverShown': 'PIN not available to display',
  'staff.setPin': 'Set PIN',
  'staff.pinSet': 'PIN updated',
  'staff.password': 'Admin password',
  'staff.passwordHelp': 'Applies to the admin role only. Stored hashed',
  'staff.save': 'Save staff member',
  'staff.deactivate': 'Deactivate',
  'staff.deactivateConfirm': 'This person will no longer be able to sign in on any terminal',
  'staff.activate': 'Reactivate',
  'staff.failedAttempts': 'Failed attempts',
  'staff.lockedUntil': 'Locked until {time}',

  'terminals.title': 'Terminals',
  'terminals.subtitle': 'Devices authorized to sell',
  'terminals.new': 'Register terminal',
  'terminals.name': 'Terminal name',
  'terminals.register': 'Register',
  'terminals.appVersion': 'App version',
  'terminals.device': 'Device',
  'terminals.deviceUnknown': 'No information',
  'terminals.lastSeen': 'Last seen',
  'terminals.lastSync': 'Last sync',
  'terminals.never': 'Never',
  'terminals.issueToken': 'Issue token',
  'terminals.issueTokenConfirm': 'A new token will be generated for this terminal. Any previous token will stop working',
  'terminals.tokenOnceTitle': 'Terminal token',
  'terminals.tokenOnceWarning': 'This token will not be shown again. Copy it now and set it on the terminal',
  'terminals.revoke': 'Revoke token',
  'terminals.revokeConfirm': 'The terminal will not be able to sync until it is issued a new token',
  'terminals.deactivate': 'Deactivate terminal',
  'terminals.deactivateConfirm': 'The terminal will no longer be authorized to sell',
  'terminals.activate': 'Reactivate',
  'terminals.quiet': 'No recent activity',
  'terminals.quietWarning': 'This terminal has not synced in over {hours} hours',

  // Payment terminals (Mercado Pago / Clip push) — deliberately a separate
  // namespace from "terminals" above, which is the POS device.
  'paymentTerminals.title': 'Payment terminals',
  'paymentTerminals.subtitle': 'Mercado Pago and Clip terminals for charging without typing the authorization number',
  'paymentTerminals.new': 'Register payment terminal',
  'paymentTerminals.processor': 'Provider',
  'paymentTerminals.processorMercadoPago': 'Mercado Pago',
  'paymentTerminals.processorClip': 'Clip',
  'paymentTerminals.externalTerminalId': 'Terminal identifier',
  'paymentTerminals.externalTerminalIdHintMercadoPago': "Mercado Pago's terminal_id (PDV mode)",
  'paymentTerminals.externalTerminalIdHintClip': "The Clip Total / Pin Pad's serial number",
  'paymentTerminals.displayName': 'Display name',
  'paymentTerminals.displayNameHint': 'How it will look at the register, e.g. "Register 1 — Clip Total"',
  'paymentTerminals.save': 'Save payment terminal',
  'paymentTerminals.deactivate': 'Deactivate',
  'paymentTerminals.deactivateConfirm': 'The terminal will no longer appear as a charging option at the register',
  'paymentTerminals.activateConfirm': 'The terminal will appear as a charging option at the register again',
  'paymentTerminals.activate': 'Reactivate',
  'paymentTerminals.empty': 'No payment terminals registered yet',

  'reports.title': 'Reports',
  'reports.subtitle': 'Sales, discounts and performance by period',
  'reports.period.today': 'Today',
  'reports.period.week': 'This week',
  'reports.period.month': 'This month',
  'reports.period.custom': 'Custom period',
  'reports.from': 'From',
  'reports.to': 'To',
  'reports.filterMethod': 'Payment method',
  'reports.filterStaff': 'Staff',
  'reports.filterTerminal': 'Terminal',
  'reports.apply': 'Apply filters',
  'reports.grossSales': 'Gross sales',
  'reports.netSales': 'Net sales',
  'reports.transactions': 'Transactions',
  'reports.itemsSold': 'Items sold',
  'reports.discountTotal': 'Total discounts',
  'reports.averageTicket': 'Average ticket',
  'reports.costOfGoods': 'Cost of goods',
  'reports.netProfit': 'Net profit',
  'reports.itemsWithoutCost':
    '{count} items sold have no recorded cost; the profit figure does not subtract them. Set costs in Products for full coverage.',
  'reports.byMethod': 'By payment method',
  'reports.byReason': 'Discounts by reason',
  'reports.topByUnits': 'Top items by units',
  'reports.topByRevenue': 'Top items by revenue',
  'reports.noData': 'No data in this period',
  'reports.methodCash': 'Cash',
  'reports.methodCard': 'Card',

  'shifts.title': 'Shift reconciliation',
  'shifts.subtitle': 'Declared cash against expected cash, per shift. Admin only',
  'shifts.staff': 'Staff',
  'shifts.terminal': 'Terminal',
  'shifts.opened': 'Opened',
  'shifts.closed': 'Closed',
  'shifts.status': 'Status',
  'shifts.statusOpen': 'Open',
  'shifts.statusClosed': 'Closed',
  'shifts.openingBalance': 'Opening balance',
  'shifts.closingBalance': 'Declared cash',
  'shifts.expectedCash': 'Expected cash',
  'shifts.variance': 'Variance',
  'shifts.varianceOver': 'Over by {amount}',
  'shifts.varianceShort': 'Short by {amount}',
  'shifts.varianceNone': 'No variance',
  'shifts.noData': 'No shifts in this period',
  'shifts.confidential': 'This screen is admin-only. Staff never see this information',

  'sync.title': 'Sync health',
  'sync.subtitle': 'Pending and rejected records by terminal',
  'sync.terminal': 'Terminal',
  'sync.pendingCount': 'Pending uploads',
  'sync.rejectedTitle': 'Rejected records',
  'sync.rejectedEmpty': 'No rejected records',
  'sync.rejectedCode': 'Code',
  'sync.rejectedMessage': 'Reason',
  'sync.rejectedAt': 'Received',
  'sync.rejectedKind': 'Kind',
  'sync.needsAttention': 'Needs attention',
  'sync.allClear': 'All synced',
  'sync.rejectedAction': 'Action',
  'sync.resolve': 'Mark resolved',

  'impersonation.start': 'View as this person',
  'impersonation.confirmTitle': 'View the system as {name}',
  'impersonation.confirmBody': 'You will see the interface exactly as this person sees it, in read-only mode. Nothing you do while viewing will be saved',
  'impersonation.confirmButton': 'Enter view mode',
  'impersonation.bannerLabel': 'Viewing as: {name} — read only',
  'impersonation.exit': 'Exit view mode',
  'impersonation.readOnlyNotice': 'This action is disabled while viewing',

  'error.network': 'Could not reach the server',
  'error.unauthorized': 'Your session expired. Sign in again',
  'error.forbidden': 'You do not have permission for this action',
  'error.validation': 'Check the data you entered',
  'error.notFound': 'Resource not found',
  'error.unknown': 'An unexpected error occurred',

  'error.boundaryTitle': 'Something went wrong',
  'error.boundaryMessage':
    'This screen hit an unexpected error. Nothing you had entered was lost. Reload the page and try again',
  'error.reload': 'Reload page',

  'validation.minLength': 'At least {min} characters',
  'validation.invalidEmail': 'Invalid email address',
  'validation.fieldInvalid': 'Invalid value',
  'validation.outOfRange': 'Must be between {min} and {max}',

  'staff.passwordMinLength': 'At least 12 characters',
  'staff.passwordTooShort': 'The password must be at least 12 characters',
  'staff.passwordRequiredForAdmin': 'A password is required for the admin role',
  'staff.discountCeilingInvalid': 'Enter a number between 0 and 100, up to two decimals',
  'staff.codeInUse': 'That staff code is already in use',
  // stock & media
  'products.editTitle': 'Edit product',
  'products.duplicate': 'Duplicate',
  'products.duplicateTitle': 'Duplicate product',
  'products.duplicateSkuHint': 'Enter a SKU different from the original product',
  'products.stock': 'Stock',
  'products.stockAdjustLabel': 'Set stock to',
  'products.stockOpeningLabel': 'Opening stock',
  'products.stockInvalid': 'Enter a number, up to three decimals',
  'products.batch.toolbarLabel': 'Actions for the selection',
  'products.batch.selectAll': 'Select every filtered product',
  'products.batch.selectRow': 'Select {name}',
  'products.batch.selectedCount': '{count} selected',
  'products.batch.activate': 'Reactivate selection',
  'products.batch.deactivate': 'Deactivate selection',
  'products.batch.clearSelection': 'Clear selection',
  'products.batch.someFailed': '{count} could not be updated',
  'categories.imageUrl': 'Image',
  'media.upload': 'Upload image',
  'media.uploading': 'Uploading…',
  'media.orUrl': 'Or paste an image URL',
  'media.clickToUpload': 'Click or press Enter to change the image',

  // Table edit mode
  'products.tableEdit.toggle': 'Table edit mode',
  'products.tableEdit.save': 'Save changes',
  'products.tableEdit.saving': 'Saving…',
  'products.tableEdit.pendingCount': '{count} rows unsaved',
  'products.tableEdit.cancelTitle': 'Cancel table edit mode',
  'products.tableEdit.discardConfirm': 'Discard changes',
  'products.tableEdit.cancelConfirm': 'Unsaved changes in the marked rows will be lost. This cannot be undone',
  'products.tableEdit.unsaved': 'Unsaved',
  'products.tableEdit.nameSkuRequired': 'Name and SKU cannot be left empty',
  'products.tableEdit.rowStatus': 'Row status',
  'products.tableEdit.rowFailed': 'Not saved',
  'products.tableEdit.fieldLabel': '{field}: {name}',
  'products.tableEdit.saveFailed': '{count} rows could not be saved',

  // Terminal auto-provisioning panel
  'provisioning.title': 'Devices waiting to be paired',
  'provisioning.subtitle': 'Terminals announcing themselves, waiting for approval',
  'provisioning.empty': 'No devices waiting for approval',
  'provisioning.name': 'Name',
  'provisioning.nameHint': 'The name must be unique. If it already exists, change it here before approving',
  'provisioning.nameRequired': 'Enter a name',
  'provisioning.uuid': 'UUID',
  'provisioning.device': 'Device',
  'provisioning.firstSeen': 'First seen',
  'provisioning.lastSeen': 'Last seen',
  'provisioning.online': 'Online',
  'provisioning.offline': 'Offline',
  'provisioning.statusPending': 'Pending',
  'provisioning.statusApproved': 'Approved, waiting on the device',
  'provisioning.statusClaimed': 'Paired',
  'provisioning.statusRejected': 'Rejected',
  'provisioning.approve': 'Approve',
  'provisioning.approving': 'Approving…',
  'provisioning.approveSuccess': 'Approved. The device will configure itself within about 30 seconds',
  'provisioning.reject': 'Reject',
  'provisioning.rejectConfirm': 'The device will not be able to pair with this request. It can announce itself again to try once more',
  'provisioning.fieldLabel': '{field}: {name}',
  'provisioning.copyUuidLabel': 'Copy UUID for {name}',

  // Table sort & filter
  'table.sortBy': 'Sort by {column}',
  'table.filterBy': 'Filter by {column}',
  'table.filterContainsPlaceholder': 'Contains…',
  'table.filterMinLabel': '{column}, minimum',
  'table.filterMaxLabel': '{column}, maximum',
  'table.clearFilters': 'Clear filters',
  'table.filtersActive': '{count} active filters',
  'table.noMatches': 'No results with the current filters',

  'shifts.varianceCategoryOver': 'Over',
  'shifts.varianceCategoryShort': 'Short',

  'discountReasons.activate': 'Reactivate',
  'discountReasons.defaultPct': 'Standard percentage',
  'discountReasons.defaultPctHint':
    'Optional, 0 to 100 with up to two decimals. Picking this reason on the terminal fills the discount with this percentage; the operator can change it. Empty = no standard.',
  'discountReasons.minSubtotal': 'Minimum ticket subtotal',
  'discountReasons.minSubtotalHint':
    'Optional, in pesos. The reason is only offered on the terminal once the ticket subtotal reaches this amount. Empty = always available.',

  // Ticket template editor
  'ticket.title': 'Ticket design',
  'ticket.subtitle':
    'Printed name, header and footer. Changes reach the terminals on their next sync, within a minute while online.',
  'ticket.businessName': 'Printed business name',
  'ticket.businessNameHint': 'Printed centered in large type: 16 characters fit per line.',
  'ticket.headerLabel': 'Header',
  'ticket.headerHint':
    'Printed above the business name. Each row is one ticket line; long text wraps to 32 columns on its own.',
  'ticket.footerLabel': 'Footer',
  'ticket.footerHint':
    'Printed at the end of the ticket. Each row is one line; an empty row leaves a blank line.',
  'ticket.qrUrl': 'QR code link',
  'ticket.qrUrlHint':
    'Printed as a QR code at the very end of the ticket, after the footer. Leave empty to print no code.',
  'ticket.preview': 'Preview',
  'ticket.previewHint': 'Rendered with the same layout the printer uses, over a sample sale.',
  'ticket.save': 'Save changes',
  'ticket.saved': 'Saved. Terminals receive it on their next sync.',
  'ticket.unsaved': 'There are unsaved changes.',
  'staff.setPassword': 'Reset password',
  'staff.newPassword': 'New password',
  'staff.passwordConfirm': 'Confirm password',
  'staff.passwordMismatch': 'The passwords do not match.',
  'staff.passwordNeverShown': 'The current password is never shown; this only sets a new one.',
  'staff.passwordSet': 'Password updated. Share it with the person through a secure channel.',
  'app.newVersion': 'A newer version of this page is available.',
  'app.reloadNow': 'Reload',
};
